import { Icon } from '@iconify/react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { Card } from '../../shared/components';
import { hhmm, minutesLabel, myApi, statusStyle, Today } from '../payroll.api';

/**
 * Punch In / Punch Out for the signed-in employee. The time recorded is the server's, not the
 * browser's, so a wrong PC clock cannot move a punch.
 */
export default function PunchCard({ onChange }: { onChange?: () => void }) {
  const [today, setToday] = useState<Today | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    myApi
      .today()
      .then(setToday)
      .catch((e) => setError(logError({ message: 'Could not load today’s attendance', error: e, code: 'MY_TODAY_FAILED' })));
  }, []);

  const punch = async (kind: 'in' | 'out') => {
    setBusy(true);
    try {
      const t = kind === 'in' ? await myApi.punchIn() : await myApi.punchOut();
      setToday(t);
      toast.success(kind === 'in' ? `Punched in at ${hhmm(t.punchIn)}` : `Punched out at ${hhmm(t.punchOut)}`);
      onChange?.();
    } catch (e) {
      toast.error(logError({ message: 'Punch failed', error: e, code: 'PUNCH_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  const status = today?.status ? statusStyle(today.status) : null;

  return (
    <Card title='TODAY'>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent='space-between'>
        <Stack gap={0.5}>
          <Typography variant='h4' fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            {today?.officeStart && today?.officeEnd ? ` · Office ${hhmm(today.officeStart)}–${hhmm(today.officeEnd)}` : ''}
          </Typography>
          {error && (
            <Typography variant='body2' color='error'>
              {error}
            </Typography>
          )}
        </Stack>

        <Stack direction='row' gap={3}>
          <Stack>
            <Typography variant='caption' color='text.secondary'>
              In
            </Typography>
            <Typography variant='h6'>{hhmm(today?.punchIn) || '--:--'}</Typography>
          </Stack>
          <Stack>
            <Typography variant='caption' color='text.secondary'>
              Out
            </Typography>
            <Typography variant='h6'>{hhmm(today?.punchOut) || '--:--'}</Typography>
          </Stack>
          <Stack>
            <Typography variant='caption' color='text.secondary'>
              Worked
            </Typography>
            <Typography variant='h6'>{minutesLabel(today?.workedMinutes) || '—'}</Typography>
          </Stack>
          {status && (
            <Stack>
              <Typography variant='caption' color='text.secondary'>
                Status
              </Typography>
              <Typography variant='h6' sx={{ color: status.fg }}>
                {status.label}
              </Typography>
            </Stack>
          )}
        </Stack>

        <Stack direction='row' gap={1.5}>
          <Button
            size='large'
            variant='contained'
            color='success'
            startIcon={<Icon icon='mdi:login' />}
            disabled={busy || !today?.canPunchIn}
            onClick={() => punch('in')}
            sx={{ px: 4, py: 1.5 }}
          >
            Punch In
          </Button>
          <Button
            size='large'
            variant='contained'
            color='error'
            startIcon={<Icon icon='mdi:logout' />}
            disabled={busy || !today?.canPunchOut}
            onClick={() => punch('out')}
            sx={{ px: 4, py: 1.5 }}
          >
            Punch Out
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}
